import { BaseApiClient } from './base-client.js'
import {
  API_ROUTES,
  interpolateRoute,
  type ApiResponse,
  type Conference,
  type CreateOutboundConferenceRequest,
  type UpdateOutboundCallStatusRequest,
} from '@super/shared-types'
import { withErrorHandling } from '../utils'

export interface GetSignedUrlRequest {
  url: string
  expiresInMinutes?: number
}

export interface GetSignedUrlResponse {
  signedUrl: string
  originalUrl: string
  expiresInMinutes: number
}

/**
 * Voice API Client
 * Handles voice-related API calls including recording URL conversion
 */
export class VoiceApiClient extends BaseApiClient {
  /**
   * Convert a gs:// URL to a signed URL for browser playback
   * Used for lazy loading recording URLs from outbound calls
   */
  async getSignedUrl(
    gsUrl: string,
    expiresInMinutes?: number,
  ): Promise<GetSignedUrlResponse> {
    const response = await this.request<ApiResponse<GetSignedUrlResponse>>(
      API_ROUTES.VOICE.GET_SIGNED_URL,
      {
        method: 'POST',
        body: JSON.stringify({
          url: gsUrl,
          expiresInMinutes,
        }),
      },
    )

    return response.data!
  }

  async createOutboundConference(
    dto: CreateOutboundConferenceRequest,
  ): Promise<Conference | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Conference>>(
          API_ROUTES.VOICE.CREATE_OUTBOUND_CONFERENCE,
          {
            method: 'POST',
            body: JSON.stringify(dto),
          },
        )
        return response.data || null
      },
      { errorContext: 'VoiceApiClient.createOutboundConference' },
    )
  }

  async getConferenceSignedRecordingUrl(
    conferenceId: string,
  ): Promise<{ signedUrl: string; expiresInMinutes: number }> {
    const response = await this.request<
      ApiResponse<{ signedUrl: string; expiresInMinutes: number }>
    >(
      interpolateRoute(API_ROUTES.CONFERENCES.GET_SIGNED_RECORDING_URL, {
        id: conferenceId,
      }),
    )
    return response.data!
  }

  async getConferenceSignedRecordingUrlAsAdmin(
    conferenceId: string,
  ): Promise<{ signedUrl: string; expiresInMinutes: number }> {
    const response = await this.request<
      ApiResponse<{ signedUrl: string; expiresInMinutes: number }>
    >(
      interpolateRoute(API_ROUTES.ADMIN.CONFERENCES.GET_SIGNED_RECORDING_URL, {
        id: conferenceId,
      }),
    )
    return response.data!
  }

  async updateOutboundCallStatus(
    callId: string,
    dto: UpdateOutboundCallStatusRequest,
  ): Promise<void> {
    await withErrorHandling(
      async () => {
        await this.request<ApiResponse<void>>(
          API_ROUTES.VOICE.UPDATE_OUTBOUND_CALL_STATUS.replace(
            ':callId',
            callId,
          ),
          {
            method: 'POST',
            body: JSON.stringify(dto),
          },
        )
      },
      { errorContext: 'VoiceApiClient.updateOutboundCallStatus' },
    )
  }
}

// Export singleton instance
export const voiceClient = new VoiceApiClient()
